// Import react elements
import { useEffect, useMemo, useState } from "react";

// Import components
import CreateButton from "global/components/CreateButton";

// Import utility functions
import { FormatCurrency } from "global/utilities/ConvertCurrency";

import "./RefundOrderPopUp.css";
import { CloseIcon } from "global/strich_icons/StrichIcons";



const RefundOrderPopUp = ({
    orderDetails,
    orderCurrency,
    refundingOrder,
    onClose,
    onRefund,
    refundShipping,
    setRefundShipping,
    coreCurrencies,
    currentRefundedAmount,
}) => {


    // This stores the selected refund type
    const [refundType, setRefundType] = useState("full");

    // This stores selected refund item quantities by order item id
    const [refundItemQuantities, setRefundItemQuantities] = useState({});

    // This stores a custom refund amount
    const [customRefundAmount, setCustomRefundAmount] = useState("");

    // Get the order items safely without creating a new fallback array on every render
    const orderItems = useMemo(() => {

        // Return the order items if they are an array
        if (Array.isArray(orderDetails?.orderitems)) {

            return orderDetails.orderitems;

        }

        // Return an empty array when there are no order items
        return [];

    }, [orderDetails?.orderitems]);



    // Round a value to two decimal places
    const RoundToTwoDecimalPlaces = (value) => {

        // Convert the value to a number
        const number = Number(value);

        // Return 0 if the value is not a valid number
        if (Number.isNaN(number)) {

            return 0;

        }

        // Round to two decimal places
        return Math.round((number + Number.EPSILON) * 100) / 100;

    };



    // Convert a currency amount to minor units for safe comparisons
    const ConvertToMinorUnits = (value) => {

        // Convert the value to a number
        const number = Number(value);

        // Return 0 if the value is not a valid number
        if (Number.isNaN(number)) {

            return 0;

        }

        // Return the amount in pennies/cents
        return Math.round((number + Number.EPSILON) * 100);

    };


    // Get the full refund amount
    const fullRefundAmount = RoundToTwoDecimalPlaces(Number((orderDetails?.order_values?.total_inc_tax || 0) - currentRefundedAmount));

    // Get the shipping refund amount
    const shippingRefundAmount = RoundToTwoDecimalPlaces(Number(orderDetails?.shippingcostinctax || 0));



    // Set all item quantities by default
    useEffect(() => {

        // Stop if there are no order items
        if (orderItems.length === 0) { return; }

        // Check whether quantities are already set
        const hasExistingQuantities = Object.keys(refundItemQuantities || {}).length > 0;

        // Stop if quantities already exist
        if (hasExistingQuantities) { return; }

        // Create the default quantities object
        const defaultQuantities = {};

        // Loop through each order item
        orderItems.forEach((orderItem) => {

            // Set the default refund quantity to the full order item quantity
            defaultQuantities[orderItem.id] = Number(orderItem.orderquantity || 0);

        });

        // Save default quantities
        setRefundItemQuantities(defaultQuantities);

    }, [orderItems, refundItemQuantities, setRefundItemQuantities]);




    // Calculate selected item refund amount
    const selectedItemsRefundAmount = useMemo(() => {

        // Add up selected item quantities
        return orderItems.reduce((total, orderItem) => {

            // Get selected quantity
            const selectedQuantity = Number(refundItemQuantities?.[orderItem.id] || 0);

            // Get item unit price including tax
            const unitPrice = Number(orderItem.itempriceinctax || 0);

            // Return updated total
            return RoundToTwoDecimalPlaces(total + (selectedQuantity * unitPrice));

        }, 0);

    }, [orderItems, refundItemQuantities]);



    // Get the final refund amount
    const refundAmount = useMemo(() => {

        // Return the full order amount
        if (refundType === "full") { return RoundToTwoDecimalPlaces(fullRefundAmount); }

        // Return selected items plus optional shipping
        if (refundType === "partial") {

            // Return partial refund amount to two decimal places
            return RoundToTwoDecimalPlaces(selectedItemsRefundAmount + (refundShipping ? shippingRefundAmount : 0));

        }

        // Return custom amount
        if (refundType === "custom") { return RoundToTwoDecimalPlaces(Number(customRefundAmount || 0)); }

        // Return zero as fallback
        return 0;

    }, [refundType, fullRefundAmount, selectedItemsRefundAmount, refundShipping, shippingRefundAmount, customRefundAmount]);



    // Get the refund amount in minor units
    const refundAmountMinorUnits = ConvertToMinorUnits(refundAmount);

    // Get the maximum refund amount in minor units
    const fullRefundAmountMinorUnits = ConvertToMinorUnits(fullRefundAmount);

    // Check whether the refund amount is valid
    const refundAmountIsValid = refundAmountMinorUnits > 0 && refundAmountMinorUnits <= fullRefundAmountMinorUnits;



    // Handle refund type click
    const handleRefundTypeChange = (nextRefundType) => {

        // Set refund type
        setRefundType(nextRefundType);

    };



    // Handle selected quantity change
    const handleQuantityChange = (orderItemId, nextQuantity, maxQuantity) => {

        // Create safe quantity
        const safeQuantity = Math.max(0, Math.min(Number(nextQuantity || 0), Number(maxQuantity || 0)));

        // Update selected quantities
        setRefundItemQuantities({
            ...refundItemQuantities,
            [orderItemId]: safeQuantity,
        });

    };



    // Handle quantity decrease
    const handleDecreaseQuantity = (orderItemId, currentQuantity, maxQuantity) => {

        // Decrease the selected quantity
        handleQuantityChange(orderItemId, Number(currentQuantity || 0) - 1, maxQuantity);

    };



    // Handle quantity increase
    const handleIncreaseQuantity = (orderItemId, currentQuantity, maxQuantity) => {

        // Increase the selected quantity
        handleQuantityChange(orderItemId, Number(currentQuantity || 0) + 1, maxQuantity);

    };



    // Handle submit
    const handleSubmit = () => {

        // Stop if amount is invalid
        if (!refundAmountIsValid) { return; }

        // Send refund amount back to parent
        onRefund(refundAmount);

    };



    // Return the popup
    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--medium">

                <div className="popup__header">

                    <div className="popup__headerLeft">

                        <h2 className="popup__title">Refund Order</h2>

                        <p className="popup__subtitle">
                            Choose what should be refunded. Item quantities are selected by default.
                        </p>

                    </div>

                    <div className="popup__headerRight">

                        <button type="button" className="popup__close" onClick={onClose} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>

                <div className="popup-content popup-content__body">

                    <div className="refundPopUp">

                        <div className="refundPopUp__hero">

                            <span className="refundPopUp__heroLabel">Refund amount</span>

                            <strong className="refundPopUp__heroValue">{FormatCurrency(orderCurrency, refundAmount)}</strong>

                            <span className={`refundPopUp__heroHint ${refundAmount > fullRefundAmount ? "warning-message" : ""}`}>Maximum {FormatCurrency(orderCurrency, fullRefundAmount)}</span>

                            {/* If there is a current refunded amount then display it  */}
                            {currentRefundedAmount !== 0 &&
                            
                                <>
                                    <span className="refundPopUp__heroLabel">Total previous refunds: {FormatCurrency(orderCurrency, currentRefundedAmount)}</span>
                                </>
                            
                            }

                        </div>

                        <div className="refundPopUp__optionStack">

                            <div
                                type="button"
                                className={`refundPopUp__optionCard ${refundType === "full" ? "refundPopUp__optionCard--active" : ""}`}
                                onClick={() => handleRefundTypeChange("full")}
                            >

                                <div className="horizontal-align-content-center flex-justify-left-right">

                                    <div className="refundPopUp__optionCard-heading">

                                        <strong>Full refund</strong>
                                        <span>Refund the full order total, including items and shipping.</span>
                                        
                                    </div>

                                    <div>

                                        <strong>{FormatCurrency(orderCurrency, fullRefundAmount)}</strong>
                                    
                                    </div>

                                </div>
                               
                            </div>

                            <div
                                className={`refundPopUp__optionCard ${refundType === "partial" ? "refundPopUp__optionCard--active" : ""}`}
                                onClick={() => handleRefundTypeChange("partial")}
                            >

                                <div className="refundPopUp__optionCard-header">

                                    <div className="refundPopUp__optionCard-heading">

                                        <strong>Partial refund</strong>
                                        <span>Choose item quantities, shipping, or both.</span>

                                    </div>

                                    <strong>{FormatCurrency(orderCurrency, selectedItemsRefundAmount + (refundShipping ? shippingRefundAmount : 0))}</strong>

                                </div>

                                {refundType === "partial" && (

                                    <>

                                        <br />

                                        <div className="refundPopUp__items">

                                            {orderItems.map((orderItem) => {

                                                // Get max item quantity
                                                const maxQuantity = Number(orderItem.orderquantity || 0);

                                                // Get selected quantity
                                                const selectedQuantity = Number(refundItemQuantities?.[orderItem.id] || 0);

                                                // Check if item is selected
                                                const itemIsSelected = selectedQuantity > 0;

                                                // Get selected item total
                                                const selectedTotal = selectedQuantity * Number(orderItem.itempriceinctax || 0);

                                                // Return item row
                                                return (

                                                    <div key={orderItem.id} className={`refundPopUp__itemCard ${itemIsSelected ? "refundPopUp__itemCard--selected" : "refundPopUp__itemCard--muted"}`}>

                                                        <div className="refundPopUp__itemMain">

                                                            <strong>{orderItem.title || "Untitled item"}</strong>

                                                            <span>{orderItem.sku || orderItem.pod_product_sku || ""}</span>

                                                            <small>{FormatCurrency(orderCurrency, orderItem.itempriceinctax || 0)} each</small>

                                                        </div>

                                                        <div className="refundPopUp__quantityControl">

                                                            <button type="button" onClick={() => handleDecreaseQuantity(orderItem.id, selectedQuantity, maxQuantity)} disabled={selectedQuantity <= 0}>
                                                                −
                                                            </button>

                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max={maxQuantity}
                                                                value={selectedQuantity}
                                                                onChange={(event) => handleQuantityChange(orderItem.id, event.target.value, maxQuantity)}
                                                            />

                                                            <button type="button" onClick={() => handleIncreaseQuantity(orderItem.id, selectedQuantity, maxQuantity)} disabled={selectedQuantity >= maxQuantity}>
                                                                +
                                                            </button>

                                                        </div>

                                                        <div className="refundPopUp__itemTotal">

                                                            <strong>{FormatCurrency(orderCurrency, selectedTotal)}</strong>

                                                            <span>of {maxQuantity}</span>

                                                        </div>

                                                    </div>

                                                );

                                            })}

                                            { (shippingRefundAmount === 0) || (shippingRefundAmount === "0") ?
                                            
                                                <label className="refundPopUp__shippingToggle">
                                                    No shipping fee was applied to this order.
                                                </label>
                                            
                                                :

                                                <label className={`refundPopUp__shippingToggle ${refundShipping ? "refundPopUp__shippingToggle--active" : ""}`}>

                                                    <input
                                                        type="checkbox"
                                                        checked={refundShipping}
                                                        onChange={(event) => setRefundShipping(event.target.checked)}
                                                    />

                                                    <span>
                                                        Include shipping refund
                                                        <strong>{FormatCurrency(orderCurrency, shippingRefundAmount)}</strong>
                                                    </span>

                                                </label>

                                            } 
                                            
                                        </div>

                                    </>

                                )}

                            </div>

                            <div
                                type="button"
                                className={`refundPopUp__optionCard ${refundType === "custom" ? "refundPopUp__optionCard--active" : ""}`}
                                onClick={() => handleRefundTypeChange("custom")}
                            >

                                <div className="refundPopUp__optionCard-header">

                                    <div className="refundPopUp__optionCard-heading">

                                        <strong>Custom amount</strong>

                                        <span>Enter a manual refund amount.</span>

                                    </div>

                                    <strong>{FormatCurrency(orderCurrency, Number(customRefundAmount || 0))}</strong>

                                </div>

                                {refundType === "custom" && (

                                    <>

                                        <br />

                                        <div className="refundPopUp__customCard">

                                            <div className="refundPopUp__customCard--input-prefix">
                                                {coreCurrencies?.[orderCurrency]?.symbol || orderCurrency}
                                            </div>

                                            <input
                                                type="number"
                                                min="0"
                                                max={fullRefundAmount}
                                                step="0.01"
                                                value={customRefundAmount}
                                                onChange={(event) => setCustomRefundAmount(event.target.value)}
                                                placeholder="0.00"
                                            />

                                        </div>

                                    </>

                                )}

                            </div>

                        </div>

                    </div>

                </div>

                <div className="tabs-footer tabs-footer--no-background horizontal-align-content-center">

                    {!refundAmountIsValid && (
                    
                        <p className="refundPopUp__error">
                            Refund amount must be greater than zero and cannot exceed the order total.
                        </p>
                      
                    )}

                    <CreateButton
                        title={refundingOrder ? "Refunding..." : `Refund ${FormatCurrency(orderCurrency, refundAmount)}`}
                        size="m"
                        type="button"
                        onClick={handleSubmit}
                        disabled={refundingOrder || !refundAmountIsValid}
                    />

                    <CreateButton
                        title="Cancel"
                        isCancel={true}
                        size="m"
                        invert
                        type="button"
                        onClick={onClose}
                        disabled={refundingOrder}
                    />

                </div>

            </div>

        </div>

    );

};

export default RefundOrderPopUp;